import { useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Trash2, Pencil } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { useHasRole } from '@/features/auth/AuthProvider';
import { fetchCustomersPage, deleteCustomer, type CustomersPage } from './customerQueries';
import { CustomerFormDialog } from './CustomerFormDialog';
import { ExcelImportButton } from '@/features/datahub/ExcelImportButton';
import { formatCurrency } from '@/lib/utils';
import type { Customer } from '@/types/schemas';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

export function CRMPage() {
  const qc = useQueryClient();
  const isOwner = useHasRole('owner');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<CustomersPage, Error, { pages: CustomersPage[] }, string[], QueryDocumentSnapshot<DocumentData> | null>({
    queryKey: ['customers'],
    queryFn: ({ pageParam }) => fetchCustomersPage(pageParam),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.cursor,
  });

  const customers = useMemo(() => data?.pages.flatMap((p) => p.customers) ?? [], [data]);

  const remove = useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      toast({ title: 'Customer deleted', variant: 'success' });
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) =>
      `${c.name} ${c.phone} ${c.email ?? ''}`.toLowerCase().includes(q),
    );
  }, [customers, search]);

  return (
    <div>
      <PageHeader
        title="CRM · Customers"
        subtitle="All customers, lifetime value, and order history."
        actions={
          <>
            <ExcelImportButton type="customers" />
            <Button onClick={() => { setEditing(null); setOpen(true); }}>
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add customer</span>
            </Button>
          </>
        }
      />

      <Card className="mb-3 p-3">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, phone, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </Card>

      {/* Desktop table */}
      <Card className="hidden sm:block">
        <div className="grid grid-cols-12 border-b bg-muted/40 px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2 text-right">Lifetime value</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">
            No customers match your search.
          </div>
        )}
        <div className="divide-y">
          {filtered.map((c) => (
            <div key={c.id} className="grid grid-cols-12 items-center px-4 py-2 text-sm hover:bg-accent/30">
              <Link to={`/crm/${c.id}`} className="col-span-4 truncate font-medium hover:underline">
                {c.name}
              </Link>
              <div className="col-span-2 truncate text-xs">{c.phone}</div>
              <div className="col-span-3 truncate text-xs text-muted-foreground">{c.email || '—'}</div>
              <div className="col-span-2 text-right tabular-nums">
                {formatCurrency(c.totalLifetimeValue ?? 0)}
                <div className="text-xs text-muted-foreground">
                  {c.orderCount ?? 0} order{(c.orderCount ?? 0) === 1 ? '' : 's'}
                </div>
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setOpen(true); }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {isOwner && (
                  <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete ${c.name}?`)) remove.mutate(c.id); }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Mobile card list */}
      <div className="space-y-2 sm:hidden">
        {isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground">No customers match.</div>
        )}
        {filtered.map((c) => (
          <Card key={c.id} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <Link to={`/crm/${c.id}`} className="min-w-0 flex-1">
                <div className="font-medium hover:underline">{c.name}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{c.phone}</div>
                {c.email && <div className="text-xs text-muted-foreground">{c.email}</div>}
                <div className="mt-1.5 text-xs">
                  <span className="text-muted-foreground">LTV: </span>
                  <span className="font-medium">{formatCurrency(c.totalLifetimeValue ?? 0)}</span>
                  <span className="ml-2 text-muted-foreground">
                    {c.orderCount ?? 0} order{(c.orderCount ?? 0) === 1 ? '' : 's'}
                  </span>
                </div>
              </Link>
              <div className="flex shrink-0 gap-1">
                <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setOpen(true); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                {isOwner && (
                  <Button variant="ghost" size="icon" onClick={() => { if (confirm(`Delete ${c.name}?`)) remove.mutate(c.id); }}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {hasNextPage && (
        <div className="mt-4 flex flex-col items-center gap-1">
          <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
          {search.trim() && (
            <p className="text-xs text-muted-foreground">
              Search only covers customers loaded so far — load more to search further.
            </p>
          )}
        </div>
      )}

      <CustomerFormDialog open={open} onOpenChange={setOpen} editing={editing} />
    </div>
  );
}
